import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Popover,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import logo from "../../assets/images/TC_Logo1.png";
import { useSnackbar } from "notistack";
import { ROUTES } from "utils/constants";
import { getRequest } from "network";
import { PROFILE_SUMMARY } from "store/apis";
import DeleteAccountDialog from "components/DeleteAccountDialog"
import Reassessment from "components/Reassessment";
import LogoutConfirmationModal from "components/LogoutConfirmationModal";
import PromptListItem from "components/PromptListItem";
import {
  fetchPromptsSummaryStart,
  setActiveFeed,
} from "store/slices/promptSlice";
import { logoutStart } from "store/slices/authSlice";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardPlus,
  Eye,
  EyeOff,
  Heart,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  PanelLeftClose,
  Trash,
  User,
  Users,
} from "lucide-react";
import LoginRequiredDialog from "components/LoginRequiredDialog";

const drawerWidth = 280;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : 70,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : 70,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const collapsibleItems = [
  { key: "favourites", icon: <Heart size={18} />, label: "Favourites", color: "#EF4444" },
  { key: "seen", icon: <Eye size={18} />, label: "Seen", color: "#3B82F6" },
  { key: "unseen", icon: <EyeOff size={18} />, label: "Unseen", color: "#8B5CF6" },
];

const personalityDashboardColor = "#14B8A6";
const leadersColor = "#F97316";

export default function AppHeader({ open, onToggle }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const { userResult } = useSelector((state) => state.assessment);
  const { favourites, seen, unseen, counts } = useSelector((state) => state.prompt.promptsSummary);
  const promptDetails = useSelector((state) => state.prompt.promptDetails);

  const hasCompletedAssessment = !!userResult?.projectivePersonality;
  const isOnPersonalityDashboard = location.pathname === ROUTES.APP_PERSONALITY_DASHBOARD;
  const isOnLeadersPage = location.pathname === ROUTES.APP_LEADERS;

  const [openSections, setOpenSections] = useState({ favourites: false, seen: false, unseen: false });
  const [anchorEl, setAnchorEl] = useState(null);
  const [reassesmentDialog, setReassesmentDialog] = useState(false)
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false)
  const [logoutConfirmDialog, setLogoutConfirmDialog] = useState(false)
  const [rejectionShown, setRejectionShown] = useState(false);
  const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);
  useEffect(() => {
    if (user) dispatch(fetchPromptsSummaryStart());
  }, [dispatch, user]);

  // Surface reassessment rejection, if any
  useEffect(() => {
    if (!user || rejectionShown) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRequest(PROFILE_SUMMARY);
        if (!cancelled && res?.reassessment && res.reassessment.accepted === false) {
          enqueueSnackbar(res.reassessment.comment || "Your reassessment was rejected.", {
            variant: "warning",
            anchorOrigin: { vertical: isMobile ? 'bottom' : 'top', horizontal: isMobile ? 'center' : 'right' },
          });
          setRejectionShown(true);
        }
      } catch (err) {
        // silent fail
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, enqueueSnackbar, isMobile, rejectionShown]);

  const toggleSection = (key) => {
    // Guests cannot access sidebar sections
    if (!user) {
      enqueueSnackbar("Please login to access this feature.", {
        variant: "warning",
        anchorOrigin: { vertical: isMobile ? 'bottom' : 'top', horizontal: isMobile ? 'center' : 'right' },
      });
      return;
    }

    // Logged-in users can expand sections (even without assessment)
    const initialSectionState = { favourites: false, seen: false, unseen: false };
    if (!open) {
      onToggle();
      setOpenSections(() => ({ ...initialSectionState, [key]: true }));
      return;
    }
    setOpenSections((prev) => ({ ...initialSectionState, [key]: !prev[key] }));
  };

  const onPromptClick = (item) => {
    const isPersonalized = !item.isGeneric;

    // If not completed assessment and clicking personalized prompt -> show message
    if (!hasCompletedAssessment && isPersonalized) {
      setLoginRequiredOpen(true);
      return;
    }

    if (isMobile || isTablet) {
      onToggle();
    }

    // Navigate to prompt URL
    navigate(`/app/prompts/${item._id}`);
    dispatch(setActiveFeed(null));
  };

  const logoutListener = () => {
    dispatch(logoutStart({
      callback: (err, data) => {
        if (!err) {
          logout(data);
          navigate(ROUTES.ROOT);
        }
      },
    }));
  };

  const renderDynamicList = (items = []) => items.map((item) => {
    const isPersonalized = !item.isGeneric;
    const isLocked = !hasCompletedAssessment && isPersonalized;

    return (
      <PromptListItem
        key={item._id}
        prompt={item}
        onClick={onPromptClick}
        isLocked={isLocked}
        isSelected={promptDetails?._id === item._id}
        showNewBadge
        variant="compact"
        isLoggedIn={!!user}
      />
    );
  });

  const keyToRoute = {
    favourites: ROUTES.APP_FAVOURITES,
    seen: ROUTES.APP_SEEN,
    unseen: ROUTES.APP_UNSEEN,
  };

  const onSeeMore = (key) => {
    if (isMobile || isTablet) {
      onToggle();
    }
    navigate(keyToRoute[key]);
  };

  const ProfileMenuItem = ({ icon, label, onClick, color = "inherit", danger = false }) => (
    <Stack
      direction="row"
      alignItems="center"
      onClick={() => {
        setAnchorEl(null);
        onClick?.();
      }}
      sx={{
        px: 2,
        py: 1.25,
        cursor: "pointer",
        borderRadius: 1.5,
        mx: 1,
        mb: 0.5,
        color: danger ? "#EF4444" : color,
        transition: "all 0.15s ease",
        "&:hover": {
          bgcolor: danger ? "#FEE2E2" : "rgba(0,0,0,0.04)",
        },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: "14px", ml: 1.5, fontWeight: 450 }}>{label}</Typography>
    </Stack>
  );

  const profileBox = () => (
    <Box sx={{ py: 1 }}>
      {user ? (
        <>
          {/* User Info Header */}
          <Box sx={{ px: 2, py: 1.5, mb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }} noWrap>
              {user?.name || "User"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }} noWrap>
              {user?.email}
            </Typography>
          </Box>

         <ProfileMenuItem
          icon={<User size={18} />}
          label="View Profile"
          onClick={() => {
          navigate(ROUTES.PROFILE);
          if (isMobile || isTablet) {
          onToggle();
          }
          }}
      />
         {user && hasCompletedAssessment && (
            <ProfileMenuItem
              icon={<ClipboardPlus size={18} />}
              label="Retake Assessment"
              onClick={() => setReassesmentDialog(true)}
            />
          )}
          <Divider sx={{ my: 1 }} />
          <ProfileMenuItem
            icon={<Trash size={18} />}
            label="Delete Account"
            onClick={() => setDeleteAccountDialog(true)}
            danger
          />
          <ProfileMenuItem
            icon={<LogOut size={18} />}
            label="Logout"
            onClick={() => setLogoutConfirmDialog(true)}
            danger
          />
        </>
      ) : (
        <ProfileMenuItem
          icon={<LogIn size={18} />}
          label="Login"
          onClick={() => navigate(ROUTES.LOGIN)}
          color="#3B82F6"
        />
      )}
    </Box>
  );

  const getListDataByKey = (key) => ({ favourites, seen, unseen }[key] || []);
  const getCountByKey = (key) => counts?.[key] || 0;

  const drawerContent = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: open ? "space-between" : "center", px: '1rem', py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        {open && (
          <Stack direction="row" alignItems="center">
            <img src={logo} alt="Logo" style={{ width: "1.6rem", height: "1.6rem", marginRight: "5px", cursor: 'pointer' }} onClick={() => navigate(ROUTES.ROOT)} />
           
            <Box
            sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
            onClick={() => navigate(ROUTES.ROOT)}
            >
  
          <Typography
          variant="h6"
          sx={{
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: 1.2,
             }}
          >
          Theecastable
          </Typography>

  
          <Typography
          sx={{
          ml: 0.5,
          fontSize: "0.5rem",
          fontWeight: 700,
          px: 0.6,
          py: "1px",
          borderRadius: "4px",
          backgroundColor: "#EEF2FF",
          color: "#030211",
          position: "relative",
          top: "-6px",
          lineHeight: 1,
          }}
         >
           BETA
          </Typography>
          </Box>

          </Stack>
        )}
        <Tooltip title={open ? "Collapse" : "Expand"} arrow placement="right">
          <IconButton
            data-tour="sidebar-toggle"
            onClick={onToggle}
            size="small"
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            sx={{
              width: 30,
              height: 30,
              p: 0,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              "&:hover": {
                bgcolor: "grey.100",
              },
            }}
          >
            {open ? <PanelLeftClose size={18} /> : <Menu size={18} />}
          </IconButton>
        </Tooltip>
      </Box>


      {/* Sections */}
      <Box data-tour="sidebar" sx={{ px: 1.5, py: 1 }}>
        <Box sx={{ mb: 1 }} data-tour="sidebar-leaders">
          <Stack
            direction="row"
            onClick={() => {
              if (isMobile || isTablet) onToggle();
              navigate(ROUTES.APP_LEADERS);
            }}
            alignItems="center"
            sx={{
              cursor: "pointer",
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: isOnLeadersPage ? `${leadersColor}10` : "transparent",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: `${leadersColor}15`,
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: `${leadersColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: leadersColor,
                flexShrink: 0,
              }}
            >
              <Users size={18} />
            </Box>

            {open && (
              <>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, ml: 1.5, flex: 1 }}>
                  Leaders
                </Typography>
                <ChevronRightIcon size={18} color="#666" />
              </>
            )}
          </Stack>
        </Box>

        {collapsibleItems.map(({ key, icon, label, color }) => (
          <Box key={key} sx={{ mb: 1 }} {...(key === "favourites" ? { "data-tour": "sidebar-favourites" } : {})}>
            {/* Section Header */}
            <Stack
              direction="row"
              onClick={() => toggleSection(key)}
              alignItems="center"
              sx={{
                cursor: user ? "pointer" : "not-allowed",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                opacity: user ? 1 : 0.5,
                bgcolor: openSections[key] ? `${color}10` : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: user ? `${color}15` : "transparent",
                },
              }}
            >
              {/* Icon with color */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: `${color}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: color,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>

              {open && (
                <>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, ml: 1.5, flex: 1 }}>
                    {label}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        bgcolor: color,
                        color: "#fff",
                        borderRadius: "10px",
                        px: 1,
                        py: 0.25,
                        minWidth: 20,
                        textAlign: "center",
                      }}
                    >
                      {getCountByKey(key)}
                    </Box>
                    {openSections[key] ? (
                      <ChevronDownIcon size={18} color="#666" />
                    ) : (
                      <ChevronRightIcon size={18} color="#666" />
                    )}
                  </Box>
                </>
              )}
            </Stack>

            {/* Expanded Content */}
            <Collapse in={open && openSections[key]} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1.5, pr: 0.5, pt: 0.5 }}>
                {renderDynamicList(getListDataByKey(key))}
                {getCountByKey(key) > 5 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    onClick={() => onSeeMore(key)}
                    sx={{
                      cursor: "pointer",
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      color: color,
                      fontWeight: 500,
                      fontSize: "13px",
                      "&:hover": { bgcolor: `${color}10` },
                    }}
                  >
                    See all {label.toLowerCase()} →
                  </Stack>
                )}
              </Box>
            </Collapse>
          </Box>
        ))}

        {user && hasCompletedAssessment && (
          <Box sx={{ mb: 1 }} data-tour="sidebar-personality-dashboard">
            <Stack
              direction="row"
              onClick={() => {
                if (isMobile || isTablet) onToggle();
                navigate(ROUTES.APP_PERSONALITY_DASHBOARD);
              }}
              alignItems="center"
              sx={{
                cursor: "pointer",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: isOnPersonalityDashboard ? `${personalityDashboardColor}10` : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: `${personalityDashboardColor}15`,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: `${personalityDashboardColor}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: personalityDashboardColor,
                  flexShrink: 0,
                }}
              >
                <LayoutDashboard size={18} />
              </Box>

              {open && (
                <>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, ml: 1.5, flex: 1 }}>
                    Your Blueprint
                  </Typography>
                  <ChevronRightIcon size={18} color="#666" />
                </>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* User Profile Section */}
      {(isMobile || isTablet) ? profileBox() : (
        <Box data-tour="sidebar-profile" sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack
            direction="row"
            alignItems="center"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              cursor: "pointer",
              p: 1,
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            {/* Avatar */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: user
                  ? "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)"
                  : "#9CA3AF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                flexShrink: 0,
              }}
            >
              {user ? user.name?.charAt(0)?.toUpperCase() || "U" : "G"}
            </Box>

            {open && (
              <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }} noWrap>
                  {user ? user.name : "Guest"}
                </Typography>
                {user && (
                  <Typography sx={{ fontSize: "11px", color: "text.secondary" }} noWrap>
                    {user.email}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            width: isMobile ? "90vw" : "260px",
          },
        }}
      >
        {profileBox()}
      </Popover>
      <Reassessment open={reassesmentDialog} handleClose={() => setReassesmentDialog(false)} />
      <DeleteAccountDialog open={deleteAccountDialog} handleClose={() => setDeleteAccountDialog(false)} />
      <LogoutConfirmationModal
        open={logoutConfirmDialog}
        onClose={() => setLogoutConfirmDialog(false)}
        onConfirm={logoutListener}
      />
       <LoginRequiredDialog
        open={loginRequiredOpen}
        onClose={() => setLoginRequiredOpen(false)}
        reason="assessment"
      />
    </>
  );

  return (
    (isMobile || isTablet)
      ? <>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, direction: 'row', justifyContent: 'space-between', my: '0.5rem', px: '0.5rem', alignItems: 'center', }}>
          <Stack direction="row" alignItems="center">
            <img src={logo} alt="Logo" style={{ width: "1.8rem", height: "1.8rem", marginRight: "8px", cursor: 'pointer' }} onClick={() => navigate(ROUTES.ROOT)} />
            <Box
                sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
                onClick={() => navigate(ROUTES.ROOT)}
            >
            <Typography
            variant="h6"
            sx={{
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: 1.2,
            }}
            >
            Theecastable
          </Typography>

          <Typography
          sx={{
          ml: 0.5,
          fontSize: "0.5rem",
          fontWeight: 700,
          px: 0.6,
          py: "1px",
          borderRadius: "4px",
          backgroundColor: "#EEF2FF",
          color: "#000000",
          position: "relative",
          top: "-6px",
          lineHeight: 1,
        }}
      >
        BETA
        </Typography>
        </Box>

          </Stack>
          <Tooltip title={open ? "Collapse" : "Expand"} arrow placement="bottom">
            <IconButton data-tour="sidebar-toggle" onClick={onToggle} size="small" aria-label={open ? "Collapse sidebar" : "Expand sidebar"}>
              <Menu />
            </IconButton>
          </Tooltip>
        </Box>
        <MuiDrawer anchor="right" open={open} onClose={onToggle} variant="temporary" ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: '100%', maxWidth: '100%' } }}>{drawerContent()}</MuiDrawer>
      </>
      : <Drawer variant="permanent" open={open} data-tour="sidebar-drawer">{drawerContent()}</Drawer>
  );
}
